import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, AuthUser, AuthOrganization, AuthRole } from '../services/authService';

interface AuthContextType {
  user: AuthUser | null;
  organization: AuthOrganization | null;
  viewingOrganization: AuthOrganization | null; // For global admin viewing another org
  roles: AuthRole[];
  permissions: Record<string, string[]>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isGlobalAdmin: boolean;
  isOrgAdmin: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  hasPermission: (section: string, permission: string) => boolean;
  setViewingOrganization: (org: AuthOrganization | null) => void; // For global admin to switch org context
  getActiveOrganizationId: () => number | null; // Returns viewing org or user's org
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  organization: null,
  viewingOrganization: null,
  roles: [],
  permissions: {},
  isAuthenticated: false,
  isLoading: true,
  isGlobalAdmin: false,
  isOrgAdmin: false,
  login: async () => ({ success: false }),
  logout: async () => {},
  refreshSession: async () => {},
  hasPermission: () => false,
  setViewingOrganization: () => {},
  getActiveOrganizationId: () => null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<AuthOrganization | null>(null);
  const [viewingOrganization, setViewingOrganization] = useState<AuthOrganization | null>(null);
  const [roles, setRoles] = useState<AuthRole[]>([]);
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;
  const isGlobalAdmin = user?.is_global_admin === true;
  const isOrgAdmin = isGlobalAdmin || roles.some(r => r.role_type === 'org_admin' || r.slug === 'org_admin');

  // Get active organization ID (viewing org takes priority for global admin)
  const getActiveOrganizationId = useCallback((): number | null => {
    if (isGlobalAdmin && viewingOrganization) {
      return viewingOrganization.id;
    }
    return organization?.id || null;
  }, [isGlobalAdmin, viewingOrganization, organization]);

  // Initialize from localStorage
  useEffect(() => {
    const token = authService.getToken();
    if (token) {
      // Load from cache first for instant render
      setUser(authService.getUser());
      setOrganization(authService.getOrganization());
      setRoles(authService.getRoles());
      setPermissions(authService.getPermissions());
      
      // Load viewing org from sessionStorage for global admin
      const savedViewingOrg = sessionStorage.getItem('viewingOrganization');
      if (savedViewingOrg) {
        try {
          setViewingOrganization(JSON.parse(savedViewingOrg));
        } catch {}
      }
      
      // Then validate session with server
      authService.refreshSession().then(data => {
        if (data.success && data.user) {
          setUser(data.user);
          setOrganization(data.organization || null);
          setRoles(data.roles || []);
          setPermissions(data.permissions || {});
        } else {
          // Session invalid - clear everything
          authService.clearStorage();
          setUser(null);
          setOrganization(null);
          setRoles([]);
          setPermissions({});
        }
      }).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  // Persist viewing organization to sessionStorage
  const handleSetViewingOrganization = useCallback((org: AuthOrganization | null) => {
    setViewingOrganization(org);
    if (org) {
      sessionStorage.setItem('viewingOrganization', JSON.stringify(org));
    } else {
      sessionStorage.removeItem('viewingOrganization');
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authService.login(email, password);
    if (response.success && response.token) {
      // Reread from localStorage after authService sets them
      setUser(authService.getUser());
      setOrganization(authService.getOrganization());
      setRoles(authService.getRoles());
      setPermissions(authService.getPermissions());
      return { success: true };
    }
    return { success: false, error: response.error || 'Login failed' };
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setOrganization(null);
    setViewingOrganization(null);
    setRoles([]);
    setPermissions({});
    sessionStorage.removeItem('viewingOrganization');
  }, []);

  const refreshSession = useCallback(async () => {
    const data = await authService.refreshSession();
    if (data.success && data.user) {
      setUser(data.user);
      setOrganization(data.organization || null);
      setRoles(data.roles || []);
      setPermissions(data.permissions || {});
    }
  }, []);

  const hasPermission = useCallback((section: string, permission: string): boolean => {
    if (isGlobalAdmin) return true;
    const sectionPerms = permissions[section] || permissions[`grc/${section}`] || [];
    return sectionPerms.includes(permission);
  }, [isGlobalAdmin, permissions]);

  return (
    <AuthContext.Provider value={{
      user, organization, viewingOrganization, roles, permissions,
      isAuthenticated, isLoading, isGlobalAdmin, isOrgAdmin,
      login, logout, refreshSession, hasPermission,
      setViewingOrganization: handleSetViewingOrganization,
      getActiveOrganizationId,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
